import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { MemberRole } from '@warehouselogix/shared';
import type { ReactNode } from 'react';

/**
 * Shared screen chrome: title row with actions, centered loading spinner,
 * error MessageBar with Retry, an empty-state line, and the read-only notice
 * for roles below a screen's write level. Every admin screen renders through
 * these so loading / empty / error / read-only look identical.
 */

const useStyles = makeStyles({
  screen: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    width: '100%',
    maxWidth: '1080px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  titleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: tokens.spacingVerticalXXXL,
  },
  empty: {
    paddingTop: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

export interface ScreenProps {
  title: string;
  /** One-line description under the title. */
  subtitle?: string;
  /** Right-aligned header actions (e.g. an Add button). */
  actions?: ReactNode;
  children: ReactNode;
}

export function Screen({ title, subtitle, actions, children }: ScreenProps): JSX.Element {
  const styles = useStyles();
  return (
    <section className={styles.screen}>
      <div className={styles.titleRow}>
        <div className={styles.titleGroup}>
          <Text size={500} weight="semibold">
            {title}
          </Text>
          {subtitle !== undefined ? <Text size={200}>{subtitle}</Text> : null}
        </div>
        {actions !== undefined ? <div className={styles.actions}>{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function LoadingState({ label }: { label: string }): JSX.Element {
  const styles = useStyles();
  return (
    <div className={styles.spinner}>
      <Spinner label={label} />
    </div>
  );
}

export interface ErrorBarProps {
  message: string;
  /** Shown as a Retry action when provided. */
  onRetry?: () => void;
}

/** Fluent MessageBar for API failures (the contract's dev-time behavior too). */
export function ErrorBar({ message, onRetry }: ErrorBarProps): JSX.Element {
  return (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>Request failed</MessageBarTitle>
        {message}
      </MessageBarBody>
      {onRetry !== undefined ? (
        <MessageBarActions>
          <Button appearance="transparent" onClick={onRetry}>
            Retry
          </Button>
        </MessageBarActions>
      ) : null}
    </MessageBar>
  );
}

export interface ReadOnlyBarProps {
  /** The caller's role in the current tenant. */
  held: MemberRole;
  /** The role this screen's write actions need. */
  required: MemberRole;
}

/**
 * Shown when the caller's role is below the screen's write level (tenancy
 * v1). The buttons are disabled too; the server enforces the same rule.
 */
export function ReadOnlyBar({ held, required }: ReadOnlyBarProps): JSX.Element {
  return (
    <MessageBar intent="info">
      <MessageBarBody>
        <MessageBarTitle>Read-only</MessageBarTitle>
        Your role here is {held}; changes on this screen need {required}.
      </MessageBarBody>
    </MessageBar>
  );
}

export function EmptyState({ message }: { message: string }): JSX.Element {
  const styles = useStyles();
  return (
    <div className={styles.empty}>
      <Text>{message}</Text>
    </div>
  );
}
